import { ArrowUpRight, ExternalLink, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines } from "@/components/anim";
import ParallaxImage from "@/components/ParallaxImage";
import GlowCard from "@/components/GlowCard";
import Embers from "@/components/Embers";
import { MENU_URL, PHONE_TEL } from "@/site";

export default function MenuPage() {
  const { t } = useLang();
  const m = t.menuPage;

  return (
    <div>
      {/* PAGE HERO */}
      <section className="relative overflow-hidden pb-10 pt-36 sm:pt-44" data-testid="menu-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <p className="eyebrow">{m.eyebrow}</p>
          </Reveal>
          <h1 className="mt-6 font-display text-5xl italic leading-[0.98] text-white sm:text-7xl lg:text-8xl">
            <KineticLines
              mode="load"
              delay={0.15}
              lines={[m.titleA, <span className="text-gold">{m.titleB}</span>]}
            />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg">
              {m.intro}
            </p>
          </Reveal>
        </div>
      </section>

      {/* EXTERNAL MENU CARD */}
      <section className="py-10 sm:py-16" data-testid="menu-external-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <a
              href={MENU_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-external-card"
              className="group relative block overflow-hidden border border-gold/25 transition-colors duration-700 hover:border-gold/70"
            >
              <div className="relative aspect-[16/11] w-full overflow-hidden sm:aspect-[21/9]">
                <img
                  src="/img/pizza.webp"
                  alt="La pizza di Gunè"
                  loading="lazy"
                  className="h-full w-full object-cover object-center transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-ink/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              </div>

              <div className="absolute inset-0 flex flex-col justify-end p-8 sm:justify-center sm:p-16">
                <p className="eyebrow">{m.eyebrow} — Gunè</p>
                <h2 className="mt-4 max-w-2xl font-display text-4xl italic leading-[1.02] text-white sm:text-6xl">
                  {m.cardTitle}
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-300 sm:text-base">
                  {m.cardText}
                </p>
                <span className="mt-8 inline-flex w-fit items-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-black transition-colors duration-500 group-hover:bg-gold-light">
                  {m.cardCta}
                  <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </div>

              <span className="pointer-events-none absolute bottom-4 right-5 hidden items-center gap-1.5 font-mono text-[8px] uppercase tracking-[0.18em] text-white/35 sm:flex">
                <ExternalLink size={9} />
                thecubedesign.it
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              {m.note}
            </p>
          </Reveal>
        </div>
      </section>

      {/* CATEGORIES PREVIEW */}
      <section className="pb-24 pt-10 sm:pb-32 sm:pt-16" data-testid="menu-preview">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <div className="mb-12">
            <Reveal>
              <p className="eyebrow">{m.preview.eyebrow}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl italic text-white sm:text-5xl">
                {m.preview.title}
              </h2>
            </Reveal>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {m.preview.items.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1} y={44} className="h-full">
                <GlowCard
                  as="a"
                  href={MENU_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`menu-preview-card-${i}`}
                  className="group flex h-full flex-col border border-gold/10 bg-coal p-8"
                >
                  <span className="font-display text-2xl italic text-gold/40 transition-colors duration-500 group-hover:text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-14 font-display text-xl text-white sm:text-2xl">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                    {item.text}
                  </p>
                  <ArrowUpRight
                    size={18}
                    className="mt-6 text-neutral-600 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold"
                  />
                </GlowCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WINE BAND */}
      <section
        className="relative h-[55vh] overflow-hidden sm:h-[65vh]"
        data-testid="menu-wine-band"
      >
        <ParallaxImage
          src="/img/vini.webp"
          alt="La cantina dei vini di Gunè"
          strength={14}
          className="absolute inset-0 h-full"
        />
        <div className="absolute inset-0 bg-ink/60" />
        <Embers className="opacity-70" />
        <div className="relative z-10 flex h-full items-center justify-center px-5 text-center">
          <div className="max-w-2xl">
            <Reveal>
              <p className="eyebrow">{m.wine.eyebrow}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-5 font-display text-2xl italic leading-snug text-white sm:text-4xl">
                {m.wine.text}
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
